import React, { useState, useCallback, memo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Images, 
  FolderOpen, 
  FileText, 
  Users, 
  Settings, 
  Camera,
  Menu,
  X
} from 'lucide-react';
import { Button } from './ui/button';

// NavItem als separate memoized Komponente - verhindert unnötige Re-Renders
const NavItem = memo(({ item, isActive, onClick }) => {
  const Icon = item.icon;
  
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={`sidebar-link ${isActive ? 'active' : ''}`}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="w-5 h-5" />
      <span>{item.label}</span>
    </NavLink>
  );
});

NavItem.displayName = 'NavItem';

// Navigation items als Konstanten außerhalb der Komponente
const NAV_ITEMS = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/media', icon: Images, label: 'Medienbibliothek', exact: false },
  { path: '/admin/categories', icon: FolderOpen, label: 'Kategorien', exact: false },
];

const ADMIN_ONLY_ITEMS = [
  { path: '/admin/cms', icon: FileText, label: 'CMS', exact: false },
  { path: '/admin/users', icon: Users, label: 'Benutzer', exact: false },
  { path: '/admin/settings', icon: Settings, label: 'Einstellungen', exact: false },
];

const AdminSidebar = () => {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Memoized function to check if path is active
  const checkIsActive = useCallback((path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  // Memoized close handler
  const handleCloseMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // Memoized toggle handler
  const handleToggleMobile = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  // Memoized overlay click handler
  const handleOverlayClick = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // Sidebar Content als separate Komponente für Wiederverwendung
  const SidebarContent = useCallback(() => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <Camera className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-slate-900">GB Sylt</h1>
            <p className="text-xs text-slate-500">Bilddatenbank</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-1">
        <p className="px-4 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
          Hauptmenü
        </p>
        {NAV_ITEMS.map((item) => (
          <NavItem 
            key={item.path} 
            item={item} 
            isActive={checkIsActive(item.path, item.exact)}
            onClick={handleCloseMobile}
          />
        ))}

        {isAdmin() && (
          <>
            <p className="px-4 py-2 mt-6 text-xs font-medium text-slate-400 uppercase tracking-wider">
              Administration
            </p>
            {ADMIN_ONLY_ITEMS.map((item) => (
              <NavItem 
                key={item.path} 
                item={item} 
                isActive={checkIsActive(item.path, item.exact)}
                onClick={handleCloseMobile}
              />
            ))}
          </>
        )}
      </nav>

      {/* User Info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-slate-600">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 capitalize">
              {user?.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}
            </p>
          </div>
        </div>
      </div>
    </>
  ), [user, isAdmin, checkIsActive, handleCloseMobile]);

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden fixed top-4 left-4 z-50"
        onClick={handleToggleMobile}
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </Button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={handleOverlayClick}
          data-testid="mobile-overlay"
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-white transform transition-transform duration-300 lg:hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="mobile-sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Desktop Sidebar */}
      <aside 
        className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 z-30"
        data-testid="desktop-sidebar"
      >
        <SidebarContent />
      </aside>
    </>
  );
};

export default AdminSidebar;
